import type { ReactNode } from 'react';
import { cn } from '../lib/cn.js';

/**
 * PageHeader — the title row every page opens with.
 *
 * Follows the ERP reference exactly: title on the left, a pair of actions on
 * the right where the secondary carries a chevron and the primary is filled.
 * Having it as a component rather than a convention is what stops the eleventh
 * page inventing its own spacing.
 *
 * `description` sits under the title and is worth using. In an accounting tool
 * it is where a screen says what it is FOR — "every voucher traceable to the
 * event that caused it" tells someone more than the word "Accounting" does.
 */
export function PageHeader({
  title,
  description,
  actions,
  status,
  breadcrumbs,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  /** Buttons, right-aligned. Primary last, so it sits nearest the edge. */
  actions?: ReactNode;
  /** A badge beside the title — "9 checks passing", "local". */
  status?: ReactNode;
  breadcrumbs?: ReactNode;
  className?: string;
}) {
  return (
    <header className={cn('flex flex-col gap-1', className)}>
      {breadcrumbs}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex flex-col gap-1">
          <div className="flex items-center gap-2.5">
            <h1 className="text-3xl font-semibold tracking-tight text-primary">{title}</h1>
            {status}
          </div>
          {description && (
            <p className="max-w-[80ch] text-md leading-snug text-tertiary">{description}</p>
          )}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>
    </header>
  );
}

/** The page body: a max-width column with consistent padding and section gaps. */
export function PageBody({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'mx-auto flex w-full max-w-(--page-max-width) flex-col gap-(--page-section-gap)',
        'px-(--page-padding-x) py-(--page-padding-y)',
        // The page's SECTIONS arrive in sequence rather than the page arriving
        // as one slab. Each child is a card, a stat grid or a table, so the
        // cascade follows the reading order down the page — which is also the
        // order the figures depend on each other in.
        //
        // `stagger-motion` and not `enter-motion`: the whole-page version moved
        // everything together, which at any duration reads as the page being
        // redrawn rather than as content arriving.
        'stagger-motion',
        className,
      )}
    >
      {children}
    </div>
  );
}
